<template>
  <v-card class="h-100" elevation="0" rounded="lg">
    <div v-if="widgetData.loading" class="pa-6">
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" />
      <v-skeleton-loader type="divider" class="bg-transparent" />
      <v-skeleton-loader type="list-item-two-line@5" class="bg-transparent" />
    </div>

    <template v-else-if="widgetData.data">
      <v-card-title class="d-flex align-center px-6 py-3">
        <div>
          <span class="subtitle1">
            {{ $t("coroImpact.widgets.topSuspiciousDomainsWidget.title") }}
          </span>
          <span class="body2 text-indigo-medium ml-1">
            ({{ $t("coroImpact.widgets.topSuspiciousDomainsWidget.subtitle") }})
          </span>
        </div>
        <v-spacer></v-spacer>
        <a class="body2 coro-link text-decoration-none" @click="navigateToTickets()">
          <div class="view-all-btn body2 coro-link item-clickable d-flex align-center">
            <span>{{ $t("general.viewAll") }}</span>
            <v-icon icon="$chevronRight" color="link" size="16" />
          </div>
        </a>
      </v-card-title>
      <v-divider class="border-opacity-100" color="indigo-faint" />

      <v-card-text class="pb-6 px-0 h-100">
        <div
          v-if="!widgetData.data.length"
          class="d-flex flex-column align-center h-100 empty-state-wrapper"
          data-testid="top-delta-widget-no-data"
        >
          <v-icon
            class="mb-1"
            icon="$dataNoHexagon"
            size="80"
            data-testid="top-delta-widget-no-data-icon"
          ></v-icon>
          <div class="headline6 mb-1">
            {{ $t("general.noDataAvailable") }}
          </div>
        </div>
        <v-list v-else>
          <v-list-item
            v-for="source in sortedData"
            :key="source.name"
            class="py-2 px-6"
            rounded="sm"
            @click="navigateToTickets(source)"
          >
            <div class="body2">
              {{ source.name }}
            </div>
            <div class="d-flex align-center">
              <div class="bar-gradient" :style="{ width: getBarWidth(source.count) }"></div>
              <div class="value-label d-flex align-center ml-3">
                <span class="subtitle2">{{ source.count }}</span>
                <span
                  class="ml-2 d-flex align-center body2"
                  :class="getChangeInfo(source.delta).cssClass"
                  v-html="getChangeInfo(source.delta).text"
                >
                </span>
              </div>
            </div>
          </v-list-item>
        </v-list>
      </v-card-text>
    </template>

    <div v-else class="h-100 d-flex align-center justify-center flex-column pa-6">
      <v-icon icon="$alertTriangle" size="80" color="grey-lighten-1"></v-icon>
      <span class="subtitle1 mb-4 mt-4">
        {{ $t(`coroImpact.widgets.topSuspiciousDomainsWidget.error`) }}
      </span>
      <div
        class="coro-link"
        data-testid="retry-fetch-data-button"
        @click="$emit('retry-fetch-data')"
      >
        {{ $t("general.retry") }}
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { TopSuspiciousEmailSource } from "@/_store/coro-impact/coro-impact.module.ts";
import type { DataValue, TimeFrame } from "@/types.ts";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import { TicketsModuleFilter, TicketStatus } from "@/constants/tickets.ts";
import { useRouter } from "vue-router";
import { RouteName } from "@/constants/routes.ts";
import { SearchSuggestion } from "@/constants/general.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import isEmpty from "lodash/isEmpty";

export default defineComponent({
  props: {
    widgetData: {
      type: Object as PropType<DataValue<TopSuspiciousEmailSource[]>>,
      required: true,
    },
    context: {
      type: String as PropType<"single-view" | "msp">,
      default: "single-view",
    },
    filterTimeFrame: {
      type: Object as PropType<TimeFrame>,
      required: true,
    },
  },
  emits: ["retry-fetch-data"],
  setup(props) {
    const router = useRouter();
    const filtersStore = useFiltersStore();

    const sortedData = computed(() => {
      return (props.widgetData.data ?? []).sort((a, b) => b.count - a.count);
    });

    // Find the maximum count among the sources to use as the 100% baseline.
    const maxCount = computed(() => {
      const sources = props.widgetData.data ?? [];
      if (!sources || sources.length === 0) {
        return 0;
      }
      // Math.max on an array of objects
      return Math.max(...sources.map((s) => s.count));
    });

    /**
     * Calculates the proportional width of a bar as a percentage.
     * @param {number} count The value for the current item.
     * @returns {string} The width percentage string for the style attribute.
     */
    const getBarWidth = (count: number): string => {
      if (maxCount.value === 0) {
        return "0%";
      }
      const width = (count / maxCount.value) * 100;
      return `${width}%`;
    };

    /**
     * Returns an object with CSS class and text for the percentage change.
     * @param {number} delta The percentage change value.
     */
    const getChangeInfo = (delta: number) => {
      // Note: Logic implies that a decrease in suspicious emails is "good" (green)
      // and an increase is "bad" (red).
      if (delta < 0) {
        return {
          text: `(&#8595;${Math.abs(delta)}%)`,
          cssClass: "text-green-dark",
        };
      }
      if (delta > 0) {
        return {
          text: `(&#8593;${Math.abs(delta)}%)`,
          cssClass: "text-red-dark",
        };
      }
      return {
        text: "(0%)",
        cssClass: "text-indigo-medium",
      };
    };

    const navigateToTickets = (item?: TopSuspiciousEmailSource) => {
      const context = props.context === "msp" ? FilterContext.MSP_TICKETS : FilterContext.TICKETS;
      const routeName = props.context === "msp" ? RouteName.MSP_TICKETS_PAGE : RouteName.TICKETS;

      const gtmData = isEmpty(item) ? "View All" : "Domain clicked";

      trackGtmEvent(
        GtmAction.CORO_INSIGHTS_PAGE,
        `${startCase(props.context)} - ${GTMParams.TOP_SUSPICIOUS_DOMAINS_DOMAIN_CLICKED} - ${gtmData}`
      );

      filtersStore.setFilters(context, {
        eventTimeRange: {
          ...props.filterTimeFrame,
        },
        widget: TicketsModuleFilter.EMAILS_WIDGET,
        status: TicketStatus.ALL,
        search: item ? `${SearchSuggestion.DOMAIN}${item.name}` : undefined,
      });
      return router.push({ name: routeName });
    };

    return {
      sortedData,
      getBarWidth,
      getChangeInfo,
      navigateToTickets,
    };
  },
});
</script>

<style scoped lang="scss">
.bar-gradient {
  height: 4px;
  border-radius: 9999px;
  background: linear-gradient(90deg, #cd8de5 0%, #16a4f6 100%);
  transition: width 0.5s ease-in-out;
}

.view-all-btn {
  cursor: pointer;
  color: rgb(var(--v-theme-link));

  &:hover {
    text-decoration: underline;
  }
}

:deep(.empty-state-wrapper) {
  .icon-data-no-hex-l:before {
    color: rgb(var(--v-theme-indigo-light)) !important;
  }
}
</style>
